import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useSite } from '../context/SiteContext';
import { buildStorefrontNav, chromeFromColors, splitNavItems } from '../utils/storefrontChrome';
import './Header.css';

// Neutral fallback shown only when a tenant has not configured a site name yet.
// Never hardcode a specific tenant's name here — that leaks one tenant's brand
// onto every unconfigured tenant's storefront.
const FALLBACK_SITE_NAME = 'Online Store';

function NavLinkItem({ item, pathname }) {
  const children = Array.isArray(item.children) ? item.children : [];
  const hasChildren = children.length > 0;
  const childActive = children.some((c) => c.path === pathname);
  const active = pathname === item.path || childActive;

  if (!hasChildren) {
    return (
      <li>
        <Link to={item.path} className={pathname === item.path ? 'active' : ''}>
          {item.label}
        </Link>
      </li>
    );
  }

  return (
    <li className={`has-dropdown${active ? ' active' : ''}`}>
      <Link to={item.path} className={active ? 'active' : ''} aria-haspopup="true">
        {item.label}
      </Link>
      <ul className="nav-dropdown">
        {children.map((child) => (
          <li key={child.id || child.path}>
            <Link to={child.path} className={pathname === child.path ? 'active' : ''}>
              {child.label}
            </Link>
          </li>
        ))}
      </ul>
    </li>
  );
}

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { getCartCount, isCartOpen } = useCart();
  const site = useSite();

  const cartCount = getCartCount();
  const siteName = site.siteName || FALLBACK_SITE_NAME;
  const chrome = chromeFromColors(site.colors);
  const hasEcommerce = site.hasEcommerce === true;
  const logoCentered = site.logoPlacement === 'center';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const navItems = buildStorefrontNav({
    navigation: site.navigation,
    replacements: site.replacements,
    chrome,
    cartCount,
    hasEcommerce,
  });
  
  // P2: optional nav_primary_count; unset = legacy half-split (HFF-safe)
  // P5: split is over top-level items only (children live under dropdowns).
  const { primary: primaryNav, secondary: secondaryNav } = splitNavItems(
    navItems,
    site.navPrimaryCount
  );

  return (
    <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
      <div className={`header-container${logoCentered ? ' logo-center' : ''}`}>
        {/* Logo */}
        <Link to="/" className="logo">
          {site.logoUrl ? (
            <img src={site.logoUrl} alt={siteName} className="logo-image" />
          ) : (
            <span className="logo-text">{siteName}</span>
          )}
        </Link>

        {/* Mobile Menu Toggle */}
        <button 
          className={`mobile-menu-toggle ${isMenuOpen ? 'active' : ''}`}
          onClick={toggleMenu}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        {/* Navigation */}
        <nav className={`main-nav ${isMenuOpen ? 'open' : ''}`}>
          <ul className="nav-list primary-nav">
            {primaryNav.map((item, index) => (
              <NavLinkItem key={item.id || item.path || index} item={item} pathname={location.pathname} />
            ))}
          </ul>

          <ul className="nav-list secondary-nav">
            {secondaryNav.map((item, index) => (
              <NavLinkItem key={item.id || item.path || index} item={item} pathname={location.pathname} />
            ))}
          </ul>
        </nav>

        {/* Cart — icon when ecommerce is on and the tenant has no header CTA; text "Cart" is in the nav instead */}
        {hasEcommerce && !chrome.headerCtaLabel && (
          <Link to="/cart" className={`cart-link ${isCartOpen ? 'pulse' : ''}`}>
            <svg className="cart-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <path d="M16 10a4 4 0 0 1-8 0"></path>
            </svg>
            {cartCount > 0 && (
              <span className="cart-count">{cartCount}</span>
            )}
          </Link>
        )}
        {chrome.headerCtaLabel && (
          <Link to={chrome.headerCtaUrl} className="header-cta">
            {chrome.headerCtaLabel}
          </Link>
        )}
      </div>

      {/* Tagline Banner */}
      {site.tagline && (
        <div className="tagline-banner">
          <div className="container">
            <p>{site.tagline}</p>
          </div>
        </div>
      )}
    </header>
  );
}

export default Header;
